import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { expensesService } from "../../services/expenses.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IExpense } from "../../domain/IExpense";
import { IUpdatePaymentRequestParams } from "../../domain/IUpdatePaymentRequestParams";
import { IUpdateExpenseRequestParams } from "../../domain/IUpdateExpenseRequestParams";
import { ICreatePaymentRequestParams } from "../../domain/ICreatePaymentRequestParams";
import { WritableDraft } from "immer/dist/internal";
import { IExpenseSearch } from "../../domain/IExpenseSearch";
import { IGetExpensesResponse } from "../../domain/IGetExpensesResponse";
import { getIndexById, getNextSortOrder, ISortParams, sort } from "utils/sortUtils";

export const getExpensesList = createAsyncThunk(
  "Expenses/getExpensesList",
  async (data?: IExpenseSearch) => {
    try {
      return await expensesService.getAllExpenses(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const updateExpense = createAsyncThunk(
  "Expenses/updateExpense",
  async (params: {
    expenseId: number;
    data: IUpdateExpenseRequestParams;
  }) => {
    try {
      return await expensesService.updateExpense(params.expenseId, params.data);
    } catch (err) {
      setError(err);
    }
  }
);

export const createPayment = createAsyncThunk(
  "Expenses/createPayment",
  async (params: {
    expenseId: number;
    data: ICreatePaymentRequestParams;
  }) => {
    try {
      return await expensesService.createPayment(params.expenseId, params.data);
    } catch (err) {
      setError(err);
    }
  }
);

export const updatePayment = createAsyncThunk(
  "Expenses/updatePayment",
  async (params: {
    expenseId: number;
    paymentId: number;
    data: IUpdatePaymentRequestParams;
  }) => {
    try {
      return await expensesService.updatePayment(params.expenseId, params.paymentId, params.data);
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteExpense = createAsyncThunk(
  "Expenses/deleteExpense",
  async (expenseId: number) => {
    try {
      return await expensesService.deleteExpense(expenseId);
    } catch (err) {
      setError(err)
    }
  }
);

export const deletePayment = createAsyncThunk(
  "Payments/deletePayment",
  async (params: {
    expenseId: number;
    paymentId: number;
  }) => {
    try {
      return await expensesService.deletePayment(params.expenseId, params.paymentId);
    } catch (err) {
      setError(err)
    }
  }
);

interface mainState {
  expenses: IGetExpensesResponse;
  selectedIndex: number;
  sort: ISortParams | undefined;
  loading: boolean;
  newExpensePopover: IPopoverState;
  searchExpensePopover: IPopoverState;
  newPaymentPopover: IPopoverState;
  deleteExpenseModalOpen: boolean;
  deletePaymentModalOpen: boolean;
  indexForDeletion: number;
  indexForPaymentDeletion: number;
  error: any;
}

const initialState: mainState = {
  expenses: { expenses: [], expensesCount: 0, totalAmountPaid: 0.0 },
  selectedIndex: 0,
  sort: undefined,
  loading: false,
  newExpensePopover: { isOpen: false },
  searchExpensePopover: { isOpen: false },
  newPaymentPopover: { isOpen: false },
  deleteExpenseModalOpen: false,
  deletePaymentModalOpen: false,
  indexForDeletion: -1,
  indexForPaymentDeletion: -1,
  error: null,
};

export const mainSlice = createSlice({
  name: "expenses-main",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    setSort: (state, action: PayloadAction<ISortParams>) => {
      const newSortOrder = getNextSortOrder(state.sort, action.payload.key);
      const newSortParams = {
        key: action.payload.key,
        typeOfValue: action.payload.typeOfValue,
        order: newSortOrder
      };
      let selectedExpenseId = undefined;
      if (state.expenses.expenses.length > 0) {
        selectedExpenseId = state.expenses.expenses[state.selectedIndex].expenseId;
      }
      state.sort = newSortParams;
      state.expenses.expenses = sort(state.expenses.expenses, newSortParams);
      state.selectedIndex = getIndexById(
        state.expenses.expenses,
        selectedExpenseId,
        (expense: IExpense) => expense.expenseId);
    },
    setNewExpensePopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newExpensePopover = action.payload;
    },
    setSearchExpensePopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchExpensePopover = action.payload;
    },
    setNewPaymentPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newPaymentPopover = action.payload;
    },
    setDeleteExpenseModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean, index: number }>) => {
      state.deleteExpenseModalOpen = action.payload.visible;
      state.indexForDeletion = action.payload.index;
    },
    setDeletePaymentModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean, index: number }>) => {
      state.deletePaymentModalOpen = action.payload.visible;
      state.indexForPaymentDeletion = action.payload.index;
    },
    deleteExpenseById: (state, action: PayloadAction<number>) => {
      if (action.payload) {
        let expenseToDelete = state.expenses.expenses.find(expense => expense.expenseId === action.payload);

        state.expenses.expenses = state.expenses.expenses.filter(
          (expense) => expense.expenseId !== action.payload
        );
        if (expenseToDelete) {
          state.expenses.totalAmountPaid -= expenseToDelete.totalAmountPaid;
          state.expenses.expensesCount -= 1;
        }
      }
    },
    deletePaymentById: (
      state,
      action: PayloadAction<{ expenseId: number, paymentId: number }>) => {
      if (action.payload) {
        let expenseToDeleteIndex = state.expenses.expenses.findIndex(expense =>
          expense.expenseId === action.payload.expenseId);
        let paymentToDelete = state.expenses.expenses[expenseToDeleteIndex].payments.find(payment => payment.paymentId === action.payload.paymentId);

        state.expenses.expenses[expenseToDeleteIndex].payments = state.expenses.expenses[expenseToDeleteIndex].payments.filter(
          (payment) => payment.paymentId !== action.payload.paymentId
        );
        if (paymentToDelete) {
          state.expenses.expenses[expenseToDeleteIndex].totalAmountPaid -= paymentToDelete.amount;
          state.expenses.totalAmountPaid -= paymentToDelete.amount;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getExpensesList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getExpensesList.fulfilled, (state, action) => {
      state.loading = false;
      state.expenses = action.payload ? action.payload : { expenses: [], expensesCount: 0, totalAmountPaid: 0.0 };
      state.selectedIndex = 0;
    });
    builder.addCase(getExpensesList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(updateExpense.fulfilled, (state, action) => {
      state.loading = false;
      updateExpenseInState(state, action);
    });
    builder.addCase(updatePayment.fulfilled, (state, action) => {
      state.loading = false;
      updateExpenseInState(state, action);
    });
    builder.addCase(createPayment.fulfilled, (state, action) => {
      state.loading = false;
      updateExpenseInState(state, action);
    });
  },
});

export const selectState = (state: RootState) => state.expenses.main;
export const selectExpense = (state: RootState) =>
  state.expenses.main.expenses.expenses[state.expenses.main.selectedIndex];
export const selectExpenseForDeletion = (state: RootState) =>
  state.expenses.main.expenses.expenses[state.expenses.main.indexForDeletion];
export const selectPaymentForDeletion = (state: RootState) =>
  state.expenses.main.expenses.expenses[state.expenses.main.selectedIndex]?.payments[
  state.expenses.main.indexForPaymentDeletion];

export const {
  setError,
  setSelectedIndex,
  setSort,
  setNewExpensePopover,
  setSearchExpensePopover,
  setNewPaymentPopover,
  setDeleteExpenseModalOpen,
  setDeletePaymentModalOpen,
  deleteExpenseById,
  deletePaymentById
} = mainSlice.actions;

export default mainSlice.reducer;

const updateExpenseInState = (state: WritableDraft<mainState>, action: PayloadAction<IExpense | undefined>) => {
  const expensePotentiallyChanged = state.expenses.expenses[state.selectedIndex];
  if (expensePotentiallyChanged.expenseId === action.payload?.expenseId) {
    updateExactExpenseInState(state, state.selectedIndex, action.payload);
  } else {
    for (let i = 0; i < state.expenses.expenses.length; i++) {
      if (state.expenses.expenses[i].expenseId === action.payload?.expenseId) {
        updateExactExpenseInState(state, i, action.payload);
        break;
      }
    }
  }
}

const updateExactExpenseInState = (state: WritableDraft<mainState>, index: number, expense: IExpense) => {
  const expenseBeforeChange: IExpense = state.expenses.expenses[index];
  state.expenses.expenses[index] = expense;
  state.expenses.totalAmountPaid += expense.totalAmountPaid - expenseBeforeChange.totalAmountPaid;
}
